import { defineComponent as _defineComponent } from 'vue'
import { COUNTRY_WITHOUT_TITLEDETAIL } from '@/constants/web-locales.constant';
import { CollectionType } from '@/enums/collection-type';
import { WebLocale } from '@/enums/web-locale';
import { useI18n } from '@/helpers/composables/useI18n';
import { useFilterStore, useRoutingStore } from '@/helpers/composables/useStores';
import { SnowplowTitleContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { getArticleSlug } from '@/features/editorial/helper';
import Provider from '@/interfaces/provider';
import { TopArticleFragment } from '@/pages/graphql/queries/GetTopArticles.query';
import { TopTitleFragment, TopTitle_Season_Fragment } from '@/pages/graphql/queries/GetTopTitles.query';
import { computed, ref } from 'vue';
import { TranslateResult } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router/composables';
import ConsentToggleButton from '@/components/consent/ConsentToggleButton.vue';

type TopTitles = {
	movie: TopTitleFragment[];
	upcomingMovie: TopTitleFragment[];
	show: TopTitleFragment[];
	upcomingShow: TopTitleFragment[];
};


export default /*#__PURE__*/_defineComponent({
  __name: 'AppDefaultFooter',
  props: {
    locale: null,
    webLocale: null,
    topTitles: null,
    topArticles: null,
    providers: { type: Function, default: () => [] }
  },
  setup(__props: any) {

const props = __props as { locale: string, webLocale: WebLocale, topTitles: TopTitles, topArticles: TopArticleFragment[], providers: () => Provider[] };

const routesWithTitleList = [
	CollectionType.NEW,
	CollectionType.POPULAR,
	CollectionType.SEARCH,
	CollectionType.MY_LISTS,
	CollectionType.PUBLIC_LISTS,
	CollectionType.TV_SHOW_TRACKING,
];



const { setFilterValue } = useFilterStore();
const { activeDomain } = useRoutingStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const providersLimit = ref(5);
const currentYear = new Date().getFullYear();

const version = computed(() => process.env.VERSION);

const showAppFooter = computed(() => {
	const currentCollection: CollectionType = route.meta?.collectionType;
	return !routesWithTitleList.includes(currentCollection);
});

const filteredArticles = computed(() => {
	return props.topArticles.filter(article => hasTitle(article));
});

const latestEditorialHeader = computed(() => t('WEBAPP_FOOTER_TOP_X_NEWS', { x: filteredArticles.value.length }));

const shouldLinkToUpcoming = computed(() => router.getRoutes().some(({ name }) => name === 'app.titles.upcoming.list'));

const titleUrlsEnabled = computed(() => !COUNTRY_WITHOUT_TITLEDETAIL.includes(props.webLocale));

function getShowTitle(title: TopTitleFragment) {
	return [
		title.objectType === 'SHOW_SEASON' ? (title as TopTitle_Season_Fragment).show.content.title : '',
		title.content.title,
	].join(' ');
}

function handleTopProvider(provider: Provider, collectionType: CollectionType, index: number) {
	const translatable =
		collectionType === CollectionType.POPULAR
			? 'WEBAPP_FOOTER_TOP_X_PROVIDERS'
			: 'WEBAPP_FOOTER_TOP_X_NEW_ON_PROVIDER';

	trackProviderClick(provider, t(translatable, { x: providersLimit.value }), index);
	setProvider(provider, collectionType);
}

function trackHeadingClick(headline: TranslateResult) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: `${headline}`,
		property: 'footer',
		useLastRoute: true,
	});
}

function trackProviderClick(provider: Provider, headline: TranslateResult, value: number) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'tabchange',
		label: `${headline},${provider.packageId}`,
		value,
		property: 'footer',
		useLastRoute: true,
	});
}

function trackClick(item: TopTitleFragment | TopArticleFragment, headline: TranslateResult, value: number) {
	const contexts = [];

	if (item.__typename !== 'Article') {
		contexts.push(new SnowplowTitleContext(item.objectId, item.objectType));
	}
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: headline.toString(),
			value,
			property: 'footer',
			useLastRoute: true,
		},
		contexts
	);
}

function setProvider(provider: Provider, collectionType: CollectionType) {
	setFilterValue({
		filterName: 'providers',
		filterValue: [provider.shortName],
		collectionType,
		// prevents duplicate navigation
		updateRouteState: false,
	});
}
// Type Guard to check if article has a title
function hasTitle(article: TopArticleFragment): article is TopArticleFragment & { content: { title: string } } {
	return !!article.content.title;
}

return { __sfc: true,routesWithTitleList, props, setFilterValue, activeDomain, route, router, t, providersLimit, currentYear, version, showAppFooter, filteredArticles, latestEditorialHeader, shouldLinkToUpcoming, titleUrlsEnabled, getShowTitle, handleTopProvider, trackHeadingClick, trackProviderClick, trackClick, setProvider, hasTitle, CollectionType, getArticleSlug, ConsentToggleButton }
}

})